import React, { useState } from 'react';
import { Upload, X, Loader2 } from 'lucide-react';
import { FormData, Errors } from './SettingList';
import { useSettings } from '../../context/SettingsContext';
import axios from 'axios'
interface GeneralSectionProps {
    formData: FormData
    errors: Errors
    handleInputChange: (field: keyof FormData, value: string) => void
    handleImageUpload: (event: React.ChangeEvent<HTMLInputElement>) => void
    handleReset: () => void
    handleSubmit: () => void
    isLoading: boolean
}

const GeneralSection: React.FC<GeneralSectionProps> = ({
    formData,
    errors,
    handleInputChange,
    handleImageUpload,
    handleReset,
    handleSubmit,
    isLoading,
}) => {
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false)
    const [isResetModalOpen, setIsResetModalOpen] = useState<boolean>(false)
    const { fetchSettings } = useSettings();

const confirmReset = async () => {
  try {
    const token = sessionStorage.getItem("access_token");

    await axios.post(
      `${process.env.REACT_APP_API_URL}/api/settings/logo`,
      {
        setting_id: 1,
        type: "reset",
        logoname: "",
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );

    await fetchSettings();

    setIsResetModalOpen(false);
  } catch (error) {
    console.error("Reset API call failed:", error);
  }
};


    const customHandleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0]
        if (file) {
            if (file.size > 2 * 1024 * 1024) {
                console.warn("File size must be less than 2MB")
                return
            }
            const reader = new FileReader()
            reader.onload = (e) => {
                const result = (e.target as FileReader)?.result
                if (typeof result === "string") {
                    handleInputChange("logoPreview", result)
                }
            }
            reader.readAsDataURL(file)
            handleImageUpload(event)
        }
    }

    return (
        <div className="space-y-4">
            {isModalOpen && formData.logoPreview && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
                    onClick={() => setIsModalOpen(false)}
                >
                    <div
                        className="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl w-[800px] h-[600px] flex items-center justify-center"
                        onClick={(e) => e.stopPropagation()} // Prevent closing when clicking inside the modal
                    >
                        <img
                            src={formData.logoPreview || "/placeholder.svg"}
                            alt="Logo Preview"
                            className="max-w-full max-h-full object-contain p-4"
                        />
                        <button
                            onClick={() => setIsModalOpen(false)}
                            className="absolute top-2 right-2 bg-white dark:bg-gray-700 rounded-full p-1.5 hover:bg-gray-100 dark:hover:bg-gray-600 transition-all"
                        >
                            <X className="w-5 h-5 text-gray-800 dark:text-gray-200" />
                        </button>
                    </div>
                </div>
            )}

            {isResetModalOpen && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl p-4 w-full max-w-sm transform transition-all duration-200 scale-100">
                        <h3 className="text-base font-semibold text-gray-900 dark:text-white mb-3 text-center">
                            Reset General Settings
                        </h3>
                        <p className="text-xs text-gray-600 dark:text-gray-300 mb-4 text-center">
                            Are you sure you want to reset the General Section form to default values? This action cannot be undone.
                        </p>
                        <div className="flex justify-center gap-3">
                            <button
                                onClick={() => setIsResetModalOpen(false)}
                                className="px-3 py-1.5 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-gray-200 rounded-md hover:bg-gray-300 dark:hover:bg-gray-500 transition-all duration-200 text-xs"
                            >
                                No, Cancel
                            </button>
                            <button
                                onClick={confirmReset}
                                className="px-3 py-1.5 bg-green-500 text-white rounded-md hover:bg-green-600 transition-all duration-200 text-xs"
                            >
                                Yes, Reset
                            </button>
                        </div>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Company Logo</label>
                    {/* <p className="text-xs text-gray-500 dark:text-gray-400 mb-0.5">Logo upload is optional</p> */}
                    <div className="mt-0.5 flex items-center">
                        {formData.logoPreview && (
                            <img
                                src={formData.logoPreview || "/placeholder.svg"}
                                alt="Logo Preview"
                                className="h-12 w-12 object-contain mr-3 cursor-pointer"
                            />
                        )}
                        <label className="cursor-pointer bg-blue-500 text-white px-3 py-1.5 rounded-md flex items-center text-xs hover:bg-blue-600">
                            <Upload className="w-3 h-3 mr-1" />
                            Upload Logo
                            <input
                                type="file"
                                accept="image/jpeg,image/png,image/jpg,image/svg+xml,image/webp"
                                onChange={customHandleImageUpload}
                                className="hidden"
                            />
                        </label>
                    </div>
                    {(errors.logoPreview || errors.company_logo) && (
                        <p className="mt-0.5 text-xs text-red-600">{errors.company_logo || errors.logoPreview}</p>
                    )}
                </div>

                <div>
                    <label className="block text-xs font-medium text-gray-700 dark:text-gray-300">Logo Name</label>
                    <input
                        type="text"
                        value={formData.logoName}
                        onChange={(e) => handleInputChange("logoName", e.target.value)}
                        className="mt-0.5 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white shadow-sm focus:border-blue-500 focus:ring-blue-500 text-xs"
                        
                    />
                    {errors.logoName && <p className="mt-0.5 text-xs text-red-600">{errors.logoName}</p>}
                </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
                <button
                    onClick={() => setIsResetModalOpen(true)}
                    className="bg-white hover:bg-gray-100 text-black px-3 py-1.5 rounded-md font-medium transition-all shadow-sm text-xs border border-gray-300"
                    disabled={isLoading}
                >
                    Reset
                </button>
                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent rounded-md shadow-sm text-xs font-medium text-white bg-[#2F6AA7] hover:bg-[#265891] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2F6AA7] disabled:opacity-50"
                >
                    {isLoading ? <Loader2 className="w-3 h-3 mr-0.5 animate-spin" /> : null}
                    Save General Settings
                </button>
            </div>
        </div>
    )
}

export default GeneralSection
 